import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text.tsx";
import Button from "common/Buttons/Button.tsx";
import ExportOutlined from "@ant-design/icons/ExportOutlined";
import AccountConfiguration from "features/configuration/accounts/AccountConfigurationDrawer/AccountConfiguration/AccountConfiguration.tsx";
import React, { useContext } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";
import { isSymbol } from "@shared/types/utils.ts";
import { getAccountExtraDetailsRoute } from "utils/routes.tsx";
import { useHistory } from "react-router-dom";
import styled, { css, useTheme } from "styled-components";
import { ExternalLink } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { newLookPhoneType } from "common/NewLook/tokens.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const AccountConfigurationPreview = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const history = useHistory();
  const { account: formAccount } = useContext(BotFormContext)!;
  const account = isSymbol(formAccount) ? undefined : formAccount;
  const accountId = account?.client_id;
  const { isNewLook } = useTheme();

  const openAccountConfigurationDrawer = () => {
    if (accountId) {
      history.push(getAccountExtraDetailsRoute(location.pathname, accountId));
    }
  };

  return (
    <AccountConfigurationWrapper>
      {isNewLook ? (
        <NewLookHeader>
          <NewLookTitle>{t("labels.account_level_configuration")}</NewLookTitle>
          <NewLookLink type="link" onClick={openAccountConfigurationDrawer}>
            {t("buttons.edit_configuration")}
            <ExternalLink size={14} strokeWidth={1.75} />
          </NewLookLink>
        </NewLookHeader>
      ) : (
        <Flex>
          <Text type="secondary" $weight={600}>
            {t("labels.account_level_configuration")}
          </Text>
          <Button type="link" onClick={openAccountConfigurationDrawer}>
            {t("buttons.edit_configuration")}
            <ExportOutlined style={{ marginLeft: 4 }} />
          </Button>
        </Flex>
      )}
      <div style={{ marginTop: 16 }}>
        <AccountConfiguration accountId={accountId} isPreview={true} />
      </div>
    </AccountConfigurationWrapper>
  );
};

export default AccountConfigurationPreview;

const AccountConfigurationWrapper = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: ${(p) => p.theme.borders.light};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-top: 20px;
      padding-top: 20px;
      border-top: ${p.theme.borders.lightest};
    `};
`;
const NewLookHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  /* Phone: the link drops under the title rather than breaking it. */
  @media (max-width: ${phoneMaxWidth}) {
    flex-wrap: wrap;
    row-gap: 4px;
  }
`;
const NewLookTitle = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  /* Phone: the section role, 17 / 500. */
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.section};
    white-space: nowrap;
  }
`;
const NewLookLink = styled(Button)`
  &.ant-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: auto;
    padding: 0;
    font-size: 13px;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
`;
